import React, { useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../contexts/UserContext";
import DefaultCourseImg from "../../Assests/Images/default-course.jpg";
import DefaultTeacherImg from "../../Assests/Images/img_avatar1.png";
import KebabMenu from "../common/KebabMenu";

// ─── Icons ────────────────────────────────────────────────────────────────────

const IconEdit = () => (
  <svg width="16" height="16" viewBox="0 0 24 24">
    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25z" fill="none" stroke="currentColor" strokeWidth="1.6" />
    <path d="M14.06 4.94l3.75 3.75" fill="none" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

const IconSchedule = () => (
  <svg width="16" height="16" viewBox="0 0 24 24">
    <rect x="3" y="4" width="18" height="17" rx="3" fill="none" stroke="currentColor" strokeWidth="1.6" />
    <line x1="3" y1="9" x2="21" y2="9" stroke="currentColor" strokeWidth="1.6" />
    <path d="M12 13v4M10 15h4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const IconPhoto = () => (
  <svg width="16" height="16" viewBox="0 0 24 24">
    <rect x="3" y="5" width="18" height="14" rx="2" fill="none" stroke="currentColor" strokeWidth="1.6" />
    <circle cx="9" cy="11" r="2" fill="none" stroke="currentColor" strokeWidth="1.6" />
    <path d="M21 17l-5-5-4 4-2-2-4 4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconReport = () => (
  <svg width="16" height="16" viewBox="0 0 24 24">
    <path d="M6 4h12v16H6z" fill="none" stroke="currentColor" strokeWidth="1.6" />
    <line x1="8.5" y1="9"    x2="15.5" y2="9"    stroke="currentColor" strokeWidth="1.6" />
    <line x1="8.5" y1="12.5" x2="15.5" y2="12.5" stroke="currentColor" strokeWidth="1.6" />
    <line x1="8.5" y1="16"   x2="13"   y2="16"   stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

const IconPublish = () => (
  <svg width="16" height="16" viewBox="0 0 24 24">
    <path d="M12 4v12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M8 8l4-4 4 4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    <rect x="4" y="12" width="16" height="8" rx="2" fill="none" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

const IconArchive = () => (
  <svg width="16" height="16" viewBox="0 0 24 24">
    <rect x="3" y="4" width="18" height="4" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
    <path d="M5 8h14v10a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V8z" fill="none" stroke="currentColor" strokeWidth="1.6" />
    <line x1="9" y1="13" x2="15" y2="13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const IconTeacher = () => (
  <svg width="16" height="16" viewBox="0 0 24 24">
    <circle cx="12" cy="8" r="3.2" fill="none" stroke="currentColor" strokeWidth="1.6" />
    <path d="M5.5 20a6.5 6.5 0 0 1 13 0" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const IconProgram = () => (
  <svg width="16" height="16" viewBox="0 0 24 24">
    <path d="M12 3l8 4.5v9L12 21l-8-4.5v-9L12 3z" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    <path d="M12 12l8-4.5M12 12v9M12 12L4 7.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
  </svg>
);

// ─── Component ────────────────────────────────────────────────────────────────

const CourseCard = ({
  course,
  onClick,
  isRegistered = false,
  // Whether the *viewer* manages this course (can edit/publish/archive/
  // add a schedule, syllabus, or image) — a teacher for their own
  // courses, or a supplier for courses it created. MyCourses.jsx always
  // passes this explicitly now. Left optional (not a hard boolean
  // default) so any caller that doesn't pass it yet — e.g. a search
  // results view — falls back to the old teacher-only check below,
  // rather than silently losing owner actions.
  isOwner,
  search = false,
  onRequestEdit,
  onPublishCourseConfirm,
  onArchiveCourseConfirm,
  onAddScheduleConfirm,
  onChangeImage,
  onShowParticipants,
  onShowSchedule,
  onOpenSyllabus,
  onViewSyllabus,
  // Supplier-only actions: reassign which team teacher teaches this
  // course, or move it between "public" and one of the supplier's own
  // GEFEN programs. Only rendered when the viewer is a supplier
  // managing their own course — a teacher managing their own course
  // never sees these, since neither concept applies to a
  // TEACHER_INDEPENDENT course.
  onAssignTeacher,
  onAssignProgram,
  isMenuOpen = false,
  onMenuOpen,
  onMenuClose,
}) => {
  const safeCourse = course && typeof course === "object" ? course : {};

  const {
    id, title, image, subject, description,
    type, price, rating, teacher,
    startDate, students, student_count,
    syllabusUrl, syllabus_url, createdByUserId,
  } = safeCourse;

  const navigate = useNavigate();
  const { user, role, userType } = useUser();
  const isTeacher = role === "teacher" || userType === "teacher";
  const isStudent = role === "student" || userType === "student";
  const isSupplierViewer = role === "supplier" || userType === "supplier";

  // The actual "can manage this course" flag. Falls back to the
  // teacher-only check only when the caller didn't pass isOwner at all,
  // so pages that already pass it (MyCourses.jsx, for both teacher and
  // supplier) are the source of truth rather than this component
  // re-deriving ownership from role alone — which is what caused a
  // supplier's own courses to render with no edit/schedule/syllabus/
  // photo/publish/archive menu at all.
  const canManage = typeof isOwner === "boolean" ? isOwner : isTeacher;

  // Managing a course is not the same as having created it. A teacher
  // who's just assigned to teach a learning-center's course
  // (SUPPLIER_INDEPENDENT/PROGRAM, teacher_id = them but
  // created_by_user_id = the supplier) is still "canManage" — they show
  // up in this teacher's course list — but they don't get the full
  // owner toolset: no editing course details, no changing the image or
  // syllabus, no publish/archive, and obviously none of the
  // supplier-only actions. They can still add the weekly schedule for
  // a group course and see the participants report — private-lesson
  // approve/reject lives on its own page, unaffected by this. A
  // missing createdByUserId (older rows, or an API that hasn't been
  // updated to return it yet) falls back to full control rather than
  // silently locking the actual owner out.
  const isCreator =
    createdByUserId == null || String(createdByUserId) === String(user?.id ?? "");
  const hasFullControl = canManage && isCreator;
  const hasLimitedControl = canManage && !isCreator;

  const btnRef = useRef(null);

  const status     = String(safeCourse.status || "").trim();
  const isArchived = status === "Closed";
  const canPublish = status === "Editing";
  const canArchive = status === "Opened";
  const isGroup    = String(type || "").toLowerCase() === "group";
  const typeText   = isGroup ? "קבוצתי" : "פרטני";

  const showBookPrivateForStudent = isStudent && !isGroup;

  const courseSrc = useMemo(
    () => (image?.toString().trim() ? image : DefaultCourseImg),
    [image]
  );

  const avatarSrc = useMemo(
    () => (teacher?.avatar?.toString().trim() ? teacher.avatar : DefaultTeacherImg),
    [teacher?.avatar]
  );

  const ratingValue = useMemo(() => {
    // The owner (teacher or supplier) sees the course's own rating;
    // everyone else sees the assigned teacher's personal rating.
    const v = Number(canManage ? rating : teacher?.rating);
    return Number.isFinite(v) && v > 0 ? v : 0;
  }, [canManage, rating, teacher?.rating]);

  const startDateText = startDate
    ? new Date(startDate).toLocaleDateString("he-IL", { month: "short", day: "numeric", year: "numeric" })
    : "—";

  const hasSyllabus = useMemo(() => {
    return ((syllabusUrl || syllabus_url || "") + "").trim().length > 0;
  }, [syllabusUrl, syllabus_url]);

  const actions = useMemo(() => {
    if (isArchived) return [{ key: "report", label: 'דו"ח משתתפים', Icon: IconReport }];

    if (hasLimitedControl) {
      // Assigned teacher on a course a learning center created:
      // add the group schedule and see who's registered — nothing
      // that touches the course's own details, media, or lifecycle,
      // and none of the supplier-only assignment actions.
      return [
        ...(isGroup ? [{ key: "schedule", label: "הוסף מערכת שעות", Icon: IconSchedule }] : []),
        { key: "report", label: 'דו"ח משתתפים', Icon: IconReport },
      ];
    }

    return [
      { key: "edit",     label: "עריכת קורס",           Icon: IconEdit     },
      // Weekly schedule belongs to the teaching teacher only — a
      // learning center (supplier) never sets it, even on its own course.
      ...(isGroup && !isSupplierViewer ? [{ key: "schedule", label: "הוסף מערכת שעות", Icon: IconSchedule }] : []),
      { key: "syllabus", label: "הוסף תוכנית לימודים", Icon: IconPhoto    },
      { key: "photo",    label: "בחר תמונת קורס",       Icon: IconPhoto    },
      { key: "report",   label: 'דו"ח משתתפים',         Icon: IconReport   },
      // Supplier-only: who teaches it, and whether it's public or part
      // of one of the supplier's GEFEN programs.
      ...(isSupplierViewer ? [
        { key: "assignTeacher", label: "שיוך מורה",         Icon: IconTeacher },
        // Public / program assignment is final once chosen.
        ...(safeCourse?.assignmentLocked ? [] : [
          { key: "assignProgram", label: "ציבורי / תוכנית", Icon: IconProgram },
        ]),
      ] : []),
      ...(canPublish ? [{ key: "publish", label: "פרסם קורס",    Icon: IconPublish }] : []),
      ...(canArchive ? [{ key: "archive", label: "העבר לארכיון", Icon: IconArchive }] : []),
    ];
  }, [isArchived, isGroup, canPublish, canArchive, isSupplierViewer, hasLimitedControl, safeCourse?.assignmentLocked]);

  const handleAction = (key) => {
    if (!safeCourse) return;
    const map = {
      edit:          () => onRequestEdit?.(safeCourse),
      publish:       () => onPublishCourseConfirm?.(safeCourse.id),
      archive:       () => onArchiveCourseConfirm?.(safeCourse.id),
      schedule:      () => { if (isGroup && !isSupplierViewer) onAddScheduleConfirm?.([]); },
      photo:         () => onChangeImage?.(safeCourse),
      report:        () => onShowParticipants?.(safeCourse),
      syllabus:      () => onOpenSyllabus?.(safeCourse),
      assignTeacher: () => onAssignTeacher?.(safeCourse),
      assignProgram: () => { if (!safeCourse?.assignmentLocked) onAssignProgram?.(safeCourse); },
    };
    map[key]?.();
  };

  return (
    <article className="flex flex-col overflow-hidden rounded-2xl bg-white ring-1 ring-gray-100 shadow-md w-full">

      {/* ── Image Header ── */}
      <div className="relative h-44 overflow-hidden sm:h-56">
        <img
          src={courseSrc}
          alt={title || "Course"}
          className="h-full w-full object-cover"
          onError={(e) => { if (e.currentTarget.src !== DefaultCourseImg) e.currentTarget.src = DefaultCourseImg; }}
        />

        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

        {/* Top-left badges */}
        <div className="absolute left-3 top-3 z-10 flex max-w-[calc(100%-56px)] flex-wrap gap-1.5">
          <span className="rounded-lg bg-gray-700/90 px-2.5 py-1 text-xs font-semibold text-white backdrop-blur-sm">
            {subject || "general"}
          </span>
          <span className="rounded-lg bg-emerald-700/50 px-2.5 py-1 text-xs font-semibold text-white backdrop-blur-sm">
            {typeText}
          </span>
        </div>

        {/* Top-right kebab menu */}
        {canManage && !search && (
          <div className="absolute right-3 top-3 z-20">
            <button
              ref={btnRef}
              type="button"
              onClick={() => isMenuOpen ? onMenuClose?.() : onMenuOpen?.()}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-white/95 text-gray-600 shadow-md transition hover:scale-105 hover:bg-white"
            >
              <svg width="18" height="18" viewBox="0 0 24 24">
                <circle cx="12" cy="5"  r="1.5" fill="currentColor" />
                <circle cx="12" cy="12" r="1.5" fill="currentColor" />
                <circle cx="12" cy="19" r="1.5" fill="currentColor" />
              </svg>
            </button>
            <KebabMenu
              open={isMenuOpen}
              onClose={() => onMenuClose?.()}
              actions={actions}
              onAction={handleAction}
              dir="rtl"
              usePortal={true}
              triggerRef={btnRef}
              position="bottom-right"
            />
          </div>
        )}

        {/* Title overlay */}
        <h3 className="absolute inset-x-4 bottom-3 z-10 line-clamp-2 text-base font-bold leading-snug text-white drop-shadow-md sm:text-lg">
          {title || "—"}
        </h3>
      </div>

      {/* ── Body ── */}
      <div className="flex flex-1 flex-col p-4 sm:p-5">

        {/* Teacher row */}
        <div className="mb-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            {teacher?.avatar ? (
              <img
                src={avatarSrc}
                alt={teacher?.name || "Teacher"}
                className="h-7 w-7 flex-shrink-0 rounded-full border-2 border-gray-100 object-cover sm:h-8 sm:w-8"
                onError={(e) => { if (e.currentTarget.src !== DefaultTeacherImg) e.currentTarget.src = DefaultTeacherImg; }}
              />
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            )}
            <span className="text-sm font-medium text-emerald-600">
              {teacher?.name || (canManage ? "טרם שויך מורה" : "מורה")}
            </span>
          </div>

          {ratingValue > 0 && (
            <div className="flex items-center gap-1 text-sm font-semibold text-gray-800">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="#FFC107">
                <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
              </svg>
              <span>{ratingValue.toFixed(1)}</span>
            </div>
          )}
        </div>

        {/* Description */}
        <p className="mb-4 line-clamp-2 text-sm leading-relaxed text-slate-500">
          {description || "תיאור הקורס יופיע כאן"}
        </p>

        {/* Stats */}
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1.5 text-xs text-gray-500 sm:text-sm">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" />
              <line x1="3" y1="10" x2="21" y2="10" />
              <circle cx="8" cy="15" r="1" fill="currentColor" />
            </svg>
            <span>{startDateText}</span>
          </div>

          {isGroup && (
            <button
              onClick={() => onShowSchedule?.(safeCourse)}
              className="flex items-center gap-1.5 rounded-full border-none bg-white px-2.5 py-1 text-xs font-medium text-blue-600 transition hover:bg-gray-50 sm:text-sm"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="3" y="4" width="18" height="18" rx="3" />
                <line x1="3" y1="9" x2="21" y2="9" />
                <circle cx="12" cy="15" r="1.5" fill="currentColor" />
              </svg>
              לוח זמנים
            </button>
          )}

          <button
            onClick={() => { if (hasSyllabus) onViewSyllabus?.(safeCourse); }}
            disabled={!hasSyllabus}
            className={`flex items-center gap-1.5 rounded-full border-none px-2.5 py-1 text-xs font-medium transition sm:text-sm ${
              hasSyllabus
                ? "bg-white text-blue-600 hover:bg-gray-50"
                : "cursor-not-allowed bg-white text-gray-400 opacity-50"
            }`}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M6 3h8l4 4v14H6z" />
              <line x1="9" y1="12" x2="15" y2="12" />
              <line x1="9" y1="16" x2="13" y2="16" />
            </svg>
            סילבוס
          </button>
        </div>

        {/* Footer */}
        <div className="mt-auto flex items-center justify-between gap-3 border-t border-gray-100 pt-4">
          <div className="flex items-baseline gap-0.5">
            <span className="text-2xl font-bold text-emerald-500 sm:text-3xl">
              {price != null ? Number(price).toFixed(0) : "—"}
            </span>
            <span className="text-xl font-bold text-emerald-500">₪</span>
            <span className="text-sm text-gray-400">/שיעור</span>
          </div>

          {!search ? (
            <div className="flex gap-2">
              {showBookPrivateForStudent && (
                <button
                  type="button"
                  onClick={() => id && navigate(`/bookPrivateLessons/${id}`)}
                  className="rounded-xl border border-emerald-100 bg-white px-3 py-2 text-xs font-semibold text-emerald-600 transition hover:bg-emerald-50 sm:px-4 sm:text-sm"
                >
                  תאם מפגשים
                </button>
              )}
              <button
                onClick={() => id && navigate(`/CourseMeetings/${id}`)}
                disabled={canPublish}
                className={`rounded-xl px-3 py-2 text-xs font-semibold text-white transition sm:px-4 sm:text-sm ${
                  canPublish
                    ? "cursor-not-allowed bg-gray-200 text-gray-400"
                    : "bg-emerald-500 hover:bg-emerald-600"
                }`}
              >
                מפגשים
              </button>
            </div>
          ) : (
            <button
              onClick={onClick}
              disabled={isRegistered}
              className={`rounded-xl px-4 py-2 text-xs font-semibold text-white transition sm:px-5 sm:text-sm ${
                isRegistered
                  ? "cursor-not-allowed bg-gray-400"
                  : "bg-emerald-500 hover:bg-emerald-600"
              }`}
            >
              {isRegistered ? "Registered" : "Register"}
            </button>
          )}
        </div>

      </div>
    </article>
  );
};

export default CourseCard;